import React, { useState } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import ChatMessage from '../components/Chat/ChatMessage';
import ChatInput from '../components/Chat/ChatInput';
import { mockChatHistory } from '../data/mockData';
import { ChatMessage as ChatMessageType } from '../types';

const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessageType[]>(mockChatHistory);
  const [fileUploaded, setFileUploaded] = useState<string | null>(null);

  const handleSendMessage = (content: string) => {
    // Add user message
    const userMessage: ChatMessageType = {
      id: messages.length + 1,
      role: 'user',
      content,
      timestamp: new Date().toISOString()
    };
    
    setMessages([...messages, userMessage]);
    
    // Simulate assistant response after a short delay
    setTimeout(() => {
      const assistantMessage: ChatMessageType = {
        id: messages.length + 2,
        role: 'assistant',
        content: `I'm analyzing your request: "${content}"\n\nThis is a simulated response from Bruno AI. In the actual application, this would be a real response based on your financial data and query.`,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    }, 1000);
  };

  const handleFileUpload = (file: File) => {
    // In a real app, we would process the file here
    setFileUploaded(file.name);
    
    // Simulate assistant response after file upload
    const assistantMessage: ChatMessageType = {
      id: messages.length + 1,
      role: 'assistant',
      content: `I've received your file: ${file.name}\n\nI'll analyze this data for you. What kind of insights would you like me to extract?`,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, assistantMessage]);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Chat Data Prep™
      </Typography>
      
      <Paper 
        elevation={3} 
        sx={{ 
          height: 'calc(100vh - 200px)', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Chat window */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Welcome message */}
          <Paper 
            sx={{ 
              p: 2, 
              mb: 3, 
              backgroundColor: 'primary.light',
              color: 'primary.contrastText'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Welcome to Chat Data Prep™
            </Typography>
            <Typography variant="body1">
              Upload your financial data files and use natural language to transform, analyze, and visualize your data.
            </Typography>
            <Divider sx={{ my: 2, borderColor: 'primary.contrastText', opacity: 0.3 }} />
            <Typography variant="body2">
              Supported file formats: CSV, Excel (.xlsx, .xls), JSON
            </Typography>
          </Paper>
          
          {/* File upload notification */}
          {fileUploaded && (
            <Box 
              sx={{ 
                p: 1, 
                mb: 2, 
                bgcolor: 'success.light', 
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Typography variant="body2">
                File uploaded: {fileUploaded}
              </Typography>
            </Box>
          )}
          
          {/* Chat messages */}
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              content={message.content}
              role={message.role}
              timestamp={message.timestamp}
            />
          ))}
        </Box>
        
        {/* Chat input */}
        <ChatInput 
          onSendMessage={handleSendMessage}
          onFileUpload={handleFileUpload}
        />
      </Paper>
    </Box>
  );
};

export default ChatPage;
